import React from 'react'
import { Box, Skeleton } from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import { __t } from '../../../../../i18n/t'
import Permission from '../../../../../components/Permission'
import { PERMISSIONS } from '../../../../../constants'
import { Audience, CITY_COVERAGE_LOW, DataState, ListOrigin, coverageText, fmtN } from '../../model'
import { useWiz } from '../../state/WizardContext'
import { Callout, FieldError, Hint } from '../../ui/basics'
import { CheckRow, OutlineButton, RadioCard, RadioRow } from '../../ui/controls'
import { MultiPicker } from '../../ui/MultiPicker'
import { C, stackStartSx } from '../../ui/tokens'

// Estilo e atributos fora do JSX: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver ui/tokens.ts).
// A etapa já pergunta "Para quem/Quando vale a promoção?": sem subtítulo que repita a pergunta.
const stepSx = { display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const panelSx = { display: 'flex', flexDirection: 'column', gap: 2, p: 2.25, border: `1px solid ${C.line}`, borderRadius: '12px', bgcolor: C.surface, maxWidth: 760 } as const
const optionSx = { display: 'flex', flexDirection: 'column', gap: 1 } as const
const optionTitleSx = { display: 'flex', flexDirection: 'column', gap: 0.25 } as const
const countBoxSx = { display: 'flex', alignItems: 'center', gap: 1.5, p: '12px 14px', borderRadius: '10px', bgcolor: '#fff', border: `1px solid ${C.line}`, maxWidth: 760 } as const
const countTextSx = { display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 } as const
const calloutSx = { maxWidth: 520, fontSize: 13.5 } as const
const wideCalloutSx = { maxWidth: 760 } as const
const TAGS_LINK = { href: '#/chains/chainId/tag/create', target: '_blank', rel: 'noopener noreferrer' }
const linkButtonSx = { height: 40, px: 1.75, fontSize: 14 } as const

/** Bloco com título e ajuda, ou com uma caixa de marcar (cidade, grupos antigos). */
const Option: React.FC<{ toggle?: { id: string; on: boolean; onChange: () => void; title: string; desc?: string }; title?: string; help?: string; children?: React.ReactNode }> = ({ toggle, title, help, children }) => (
  <Box sx={optionSx}>
    {toggle ? (
      <CheckRow id={toggle.id} checked={toggle.on} onChange={toggle.onChange} desc={toggle.desc}>{toggle.title}</CheckRow>
    ) : (
      <Box sx={optionTitleSx}>
        <Box component="span" sx={{ fontSize: 14.5, fontWeight: 500, color: C.ink }}>{title}</Box>
        {help ? <Hint sx={{ lineHeight: 1.4 }}>{help}</Hint> : null}
      </Box>
    )}
    {children}
  </Box>
)

/** A rede ainda não tem etiqueta: diz o que fazer e leva a quem pode criar (numa aba nova, para não perder o cadastro). */
const NoTags: React.FC = () => {
  const { lists } = useWiz()
  return (
    <Callout tone="warn" sx={wideCalloutSx}>
      <Box sx={stackStartSx}>
        <span>{__t('Sua rede ainda não tem etiquetas. Crie uma (abre em outra aba) e volte aqui.')}</span>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Permission permission={PERMISSIONS.TAG}>
            <OutlineButton component="a" extra={TAGS_LINK} sx={linkButtonSx}>{__t('Criar etiqueta')}</OutlineButton>
          </Permission>
          <OutlineButton onClick={lists.tags.retry} sx={linkButtonSx}>{__t('Já criei: atualizar a lista')}</OutlineButton>
        </Box>
      </Box>
    </Callout>
  )
}

/** Etiquetas (e, para redes que ainda usam, grupos). */
const TagsPanel: React.FC = () => {
  const { c, d, set, fe, lists } = useWiz()
  const showGroups = c.groups.length > 0 || d.useGroups
  const tagsEmpty = lists.tags.state === DataState.ready && c.tags.length === 0
  return (
    <>
      <Option title={__t('Etiquetas')}>
        {tagsEmpty ? <NoTags /> : null}
        <MultiPicker
          id="f-tags" kind="tags" items={c.tags} selected={d.tags} onChange={ids => set(x => ({ ...x, tags: ids }))}
          error={fe['f-tags']} errorId="f-tags-err" state={lists.tags.state} onRetry={lists.tags.retry}
        />
      </Option>
      {showGroups ? (
        <Option
          toggle={{ id: 'use-groups', on: d.useGroups, onChange: () => set(x => ({ ...x, useGroups: !x.useGroups })), title: __t('Também usar grupos de clientes (antigo)') }}
        >
          {d.useGroups ? (
            <MultiPicker id="f-groups" kind="groups" items={c.groups} selected={d.groups} onChange={ids => set(x => ({ ...x, groups: ids }))} state={lists.groups.state} onRetry={lists.groups.retry} />
          ) : null}
        </Option>
      ) : null}
    </>
  )
}

/** Cobertura do cadastro de cidade: quantos clientes têm cidade (aviso amarelo quando é pouco). */
const CityCoverage: React.FC = () => {
  const { cityStats } = useWiz()
  const stats = cityStats.data
  const text = coverageText(stats)
  const low = !!stats && stats.total > 0 && stats.withCity / stats.total < CITY_COVERAGE_LOW
  if (!text) {
    return cityStats.pending
      ? <Skeleton width={260} />
      : <Callout tone="warn" sx={calloutSx}>{__t('Só quem tem cidade no cadastro recebe.')}</Callout>
  }
  return <Callout tone={low ? 'warn' : 'info'} sx={calloutSx}>{text}</Callout>
}

/** "Limitar pela cidade do cadastro": vale para todos os clientes ou para o público das etiquetas. */
const CityPanel: React.FC = () => {
  const { c, d, set, fe, lists, cityStats } = useWiz()
  const items = React.useMemo(() => c.cities.map(city => {
    const n = cityStats.data ? (cityStats.data.cities[city.id] ?? 0) : null
    return { id: city.id, name: city.name, meta: n == null ? null : n === 1 ? __t('1 cliente') : __t('%{n} clientes', { n: fmtN(n) }) }
  }), [c.cities, cityStats.data])
  return (
    <Option
      toggle={{
        id: 'use-city', on: d.useCity, onChange: () => set(x => ({ ...x, useCity: !x.useCity })), title: __t('Limitar pela cidade do cadastro'),
      }}
    >
      {d.useCity ? (
        <>
          <CityCoverage />
          <MultiPicker
            id="f-cities" kind="cities" items={items} selected={d.cities} onChange={ids => set(x => ({ ...x, cities: ids }))}
            error={fe['f-cities']} errorId="f-cities-err" state={lists.cities.state} onRetry={lists.cities.retry}
          />
        </>
      ) : null}
    </Option>
  )
}

/** Quantos clientes a escolha alcança (e, para quem escolheu, de quem se trata). */
const CountBox: React.FC = () => {
  const { d, audienceCount: count } = useWiz()
  const some = d.audience === Audience.some
  const restricted = some || d.useCity
  const none = count.count == null
  const text = none
    ? (count.failed ? __t('Não foi possível contar agora.') : restricted ? __t('Nenhum cliente escolhido ainda') : '')
    : count.count === 1 ? __t('1 cliente') : count.exact ? __t('%{n} clientes', { n: fmtN(count.count as number) }) : __t('Até %{n} clientes', { n: fmtN(count.count as number) })
  return (
    <Box sx={countBoxSx}>
      <GroupsIcon aria-hidden sx={{ color: C.violet, flexShrink: 0 }} />
      <Box sx={countTextSx}>
        <Box aria-live="polite" sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
          {count.loading ? <Skeleton width={150} /> : text}
        </Box>
      </Box>
    </Box>
  )
}

/** Aviso de quem veio com uma lista de clientes fixa: a lista não muda aqui. */
const ListNotice: React.FC = () => {
  const { d } = useWiz()
  return (
    <Callout tone="info" icon={<GroupsIcon />} sx={wideCalloutSx}>
      {d.listOrigin === ListOrigin.rfm
        ? __t('A lista fica fixa: quem entrar no segmento depois não recebe.')
        : __t('A lista fica fixa: quem entrar depois não recebe.')}
    </Callout>
  )
}

/** Título e descrição do cartão fixo da lista de clientes, no texto de quem veio de lá. */
function listCard(origin: ListOrigin | null, n: number): string {
  if (origin === ListOrigin.rfm) return n === 1 ? __t('Lista do Painel 360 (1 cliente)') : __t('Lista do Painel 360 (%{n} clientes)', { n: fmtN(n) })
  return n === 1 ? __t('Lista de clientes (1 cliente)') : __t('Lista de clientes (%{n} clientes)', { n: fmtN(n) })
}

/** "Para quem?": todos, só certas etiquetas (com grupos antigos) e/ou certas cidades, ou a lista de clientes. */
export const AudienceSection: React.FC = () => {
  const { d, set, fe, locked } = useWiz()
  const some = d.audience === Audience.some
  const all = d.audience === Audience.all
  const unset = d.audience === Audience.unset
  const card = listCard(d.listOrigin, d.customerIds.length)
  const shown = some || all

  return (
    <Box component="section" aria-labelledby="step-q" sx={stepSx}>
      {unset ? (
        <Callout tone="warn" sx={wideCalloutSx}>
          {__t('A lista de clientes da original não é copiada. Escolha o público.')}
        </Callout>
      ) : null}
      <RadioRow id="f-aud" labelledBy="step-q" describedBy={fe['f-aud'] ? 'f-aud-err' : undefined}>
        {locked ? (
          <RadioCard name="aud" checked onChange={() => undefined} title={card} />
        ) : (
          <>
            <RadioCard name="aud" checked={all} onChange={() => set(x => ({ ...x, audience: Audience.all }))} title={__t('Todos os clientes do app')} />
            <RadioCard name="aud" checked={some} onChange={() => set(x => ({ ...x, audience: Audience.some }))} title={__t('Só clientes com certas etiquetas')} desc={__t('Ex.: Frotistas, Taxistas.')} />
          </>
        )}
      </RadioRow>
      {fe['f-aud'] ? <FieldError id="f-aud-err">{fe['f-aud']}</FieldError> : null}

      {some ? <Box sx={panelSx}><TagsPanel /></Box> : null}
      {shown ? <Box sx={panelSx}><CityPanel /></Box> : null}
      {shown ? <CountBox /> : null}
      {locked ? <ListNotice /> : null}
    </Box>
  )
}

